import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Loader2, Tag, Ticket, Percent, DollarSign, Hash, CheckCircle2 } from "lucide-react";

const blank = { code: "", type: "percent", value: 50, target: "monthly", max_redemptions: "", expires_at: "", grandfathered: true, active: true };

const TYPE_OPTS = [
  { key: "percent", label: "% off", icon: Percent },
  { key: "fixed", label: "$ off", icon: DollarSign },
  { key: "override", label: "Set price to $", icon: Hash },
];
const TARGET_OPTS = [
  { key: "monthly", label: "Monthly subscription" },
  { key: "claim", label: "Marketplace claim fee" },
  { key: "drone", label: "Drone deployment fee" },
];

function describe(t, v, tg) {
  const tl = { monthly: "monthly membership", claim: "claim fee", drone: "drone fee" }[tg] || tg;
  if (t === "percent") return `${v}% off ${tl}`;
  if (t === "fixed") return `$${v} off ${tl}`;
  return `${tl} set to $${v}`;
}

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/promo-codes").then(({ data }) => setCodes(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) { toast.error("Enter a code."); return; }
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code.trim(), type: form.type, value: Number(form.value), target: form.target,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        grandfathered: form.grandfathered, active: form.active,
      });
      toast.success("Promo code created"); setForm(blank); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const patch = async (id, updates) => { try { await api.put(`/admin/promo-codes/${id}`, updates); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const remove = async (id) => { try { await api.delete(`/admin/promo-codes/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-promo-codes">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><Ticket className="w-4 h-4 text-cobalt" /> New promo code</h3>
        <p className="text-xs text-slate-500 mb-3">Each code sets its own effect on the price point. Live preview: <span className="font-semibold text-cobalt">{describe(form.type, form.value || 0, form.target)}</span></p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block col-span-2 sm:col-span-1"><span className="text-xs text-slate-500">Code</span>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code" placeholder="FOUNDER50" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-mono outline-none focus:border-cobalt" /></label>
          <label className="block"><span className="text-xs text-slate-500">Effect</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="promo-type" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt">
              {TYPE_OPTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-slate-500">Value</span>
            <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="promo-value" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
          <label className="block"><span className="text-xs text-slate-500">Applies to</span>
            <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid="promo-target" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt">
              {TARGET_OPTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-slate-500">Max redemptions (optional)</span>
            <input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} data-testid="promo-max" placeholder="Unlimited" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
          <label className="block"><span className="text-xs text-slate-500">Expires (optional)</span>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-expires" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.grandfathered} onChange={(e) => setForm({ ...form, grandfathered: e.target.checked })} className="h-4 w-4 accent-cobalt" data-testid="promo-grandfathered" /> Lock in as grandfathered pricing</label>
          <button onClick={create} disabled={busy} data-testid="promo-create-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create code</button>
        </div>
      </div>

      <div className="space-y-3">
        {codes.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No promo codes yet.</p>}
        {codes.map((c) => (
          <div key={c.code_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-3 shadow-sm" data-testid={`promo-${c.code_id}`}>
            <div className="min-w-0">
              <div className="font-mono font-bold text-navy flex items-center gap-2"><Tag className="w-4 h-4 text-cobalt" />{c.code}
                {!c.active && <span className="text-[10px] font-semibold bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full">Inactive</span>}
                {c.grandfathered && c.target === "monthly" && <span className="text-[10px] font-semibold bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">Grandfathered</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                <span className="text-cobalt font-medium">{c.description}</span>
                <span>Redeemed {c.redeemed_count}{c.max_redemptions ? `/${c.max_redemptions}` : ""}</span>
                {c.expires_at && <span>Expires {new Date(c.expires_at).toLocaleDateString()}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => patch(c.code_id, { active: !c.active })} data-testid={`promo-toggle-${c.code_id}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">{c.active ? "Deactivate" : "Activate"}</button>
              <button onClick={() => remove(c.code_id)} data-testid={`promo-delete-${c.code_id}`} className="p-2 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
